<template>
  <AlertMessage
    :alertType="alertType ? alertType : 'empty'"
    :message="alertMessage"
    @close="alertClose"
  />

  <ConfirmDeleteModal
    :deleteMessage="confirmMessage"
    :isOpen="isDeleteModalOpen"
    :spaceName="memberToDelete?.username"
    titleText="Delete member"
    @close="closeDeleteModal"
    @confirm="removeUser"
  />

  <DataTable
    :columns="columns"
    :current-page="currentPage"
    :data="members"
    :loading="loading"
    :page-size="pageSize"
    :sort-direction="sortDirection"
    :sort-field="sortField"
    :total-items="totalItems"
    :total-pages="totalPages"
    @sort="handleSort"
    @page-change="goToPage"
    @page-size-change="handlePageSizeChange"
    @prev-page="prevPage"
    @next-page="nextPage"
  >
    <template #cell-firstName="{ item }">
      <div class="flex flex-col gap-1">
        <div
          class="flex flex-wrap items-baseline gap-x-2 text-sm font-semibold text-gray-900"
        >
          <span>{{ item.firstName }}</span>
          <span v-if="item.lastName" class="md:hidden">{{
            item.lastName
          }}</span>
        </div>

        <dl class="mt-2 flex flex-col gap-1 text-xs text-gray-500 md:hidden">
          <div>
            <span class="font-semibold text-gray-700">Username:</span>
            <span>{{ item.username || "—" }}</span>
          </div>
          <div class="break-words">
            <span class="font-semibold text-gray-700">Email:</span>
            <span>{{ item.email || "—" }}</span>
          </div>
          <div>
            <span class="font-semibold text-gray-700">Status:</span>
            <span>{{ item.isActive ? "Active" : "Inactive" }}</span>
          </div>
        </dl>
      </div>
    </template>

    <template v-if="showFilters" #filters>
      <TableFilters
        :checkboxes="filterCheckboxes"
        @checkbox-change="handleCheckboxChange"
        @clear-filters="clearFilters"
      />
    </template>

    <template #cell-isActive="{ item }">
      <span
        :class="[
          'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide',
          item.isActive
            ? 'border-emerald-100 bg-emerald-50 text-emerald-600'
            : 'border-rose-100 bg-rose-50 text-rose-600',
        ]"
      >
        {{ item.isActive ? "Active" : "Inactive" }}
      </span>
    </template>

    <template #cell-actions="{ item }">
      <div class="flex items-center justify-start gap-2">
        <TableActionButton icon="edit" title="Edit" @click="editMember(item)" />
        <TableActionButton
          icon="delete"
          title="Remove"
          variant="danger"
          @click="confirmDeleteMember(item)"
        />
      </div>
    </template>
  </DataTable>
</template>

<script>
import { getTargetUsersPaginated } from "@/services/targetUsers";
import { removeMember } from "@/services/space";
import AlertMessage from "@/components/AlertMessage.vue";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal.vue";
import DataTable from "@/components/ui/DataTable.vue";
import TableFilters from "@/components/ui/TableFilters.vue";
import TableActionButton from "@/components/ui/TableActionButton.vue";

export default {
  emits: ["edit-member", "member-deleted", "member-updated", "update-total"],
  props: {
    showFilters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      members: [],
      alertMessage: "",
      alertType: "",
      isDeleteModalOpen: false,
      memberToDelete: null,
      confirmMessage: "",
      showActiveMembers: true,
      loading: false,
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      sortField: null,
      sortDirection: null,
      sortFieldMapping: {
        firstName: "first_name",
        lastName: "last_name",
        username: "username",
        email: "email",
      },
    };
  },
  mounted() {
    this.getMembers();
  },
  computed: {
    columns() {
      return [
        {
          key: "firstName",
          label: "First Name",
          sortable: true,
          width: "180px",
          cellClass: "align-top text-sm font-semibold text-gray-900",
          skeletonClass: "w-24",
        },
        {
          key: "lastName",
          label: "Last Name",
          sortable: true,
          width: "180px",
          headerClass: "hidden md:table-cell",
          cellClass:
            "hidden md:table-cell align-top text-sm font-semibold text-gray-900",
          skeletonClass: "w-24",
        },
        {
          key: "username",
          label: "Username",
          sortable: true,
          width: "200px",
          headerClass: "hidden md:table-cell",
          cellClass: "hidden md:table-cell align-top text-sm text-gray-900",
          skeletonClass: "w-28",
        },
        {
          key: "email",
          label: "Email",
          sortable: true,
          width: "30%",
          headerClass: "hidden md:table-cell",
          cellClass:
            "hidden md:table-cell align-top text-sm text-gray-500 break-words",
          skeletonClass: "w-44",
        },
        {
          key: "isActive",
          label: "Status",
          sortable: false,
          width: "140px",
          headerClass: "hidden md:table-cell",
          cellClass: "hidden md:table-cell",
          skeletonClass: "w-20",
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
          width: "120px",
          headerClass: "text-left",
          cellClass: "align-top text-left",
          skeletonClass: "w-16",
        },
      ];
    },
    filterCheckboxes() {
      return [
        { key: "active", label: "Only Active", value: this.showActiveMembers },
      ];
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    },
  },
  components: {
    AlertMessage,
    ConfirmDeleteModal,
    DataTable,
    TableFilters,
    TableActionButton,
  },
  watch: {
    showActiveMembers: {
      handler() {
        this.currentPage = 1;
        this.getMembers();
      },
      immediate: true,
    },
  },
  methods: {
    alertClose() {
      this.alertMessage = "";
      this.alertType = "";
    },
    showAlert(alert) {
      this.alertMessage = alert.message;
      this.alertType = alert.type;
    },
    handleSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortDirection = "asc";
      }
      this.currentPage = 1;
      this.getMembers();
    },
    handleCheckboxChange({ key, value }) {
      if (key === "active") {
        this.showActiveMembers = value;
      }
    },
    handlePageSizeChange(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1;
      this.getMembers();
    },
    clearFilters() {
      this.showActiveMembers = true;
      this.currentPage = 1;
      this.getMembers();
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getMembers();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getMembers();
      }
    },
    goToPage(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.getMembers();
      }
    },
    closeDeleteModal() {
      this.isDeleteModalOpen = false;
      this.memberToDelete = null;
    },
    confirmDeleteMember(member) {
      this.memberToDelete = member;
      this.isDeleteModalOpen = true;
      this.confirmMessage = `Are you sure you want to delete this member ${member.username}? This will remove member complete from google chat.`;
    },
    editMember(member) {
      this.$emit("edit-member", member);
    },
    async getMembers() {
      this.loading = true;
      const startTime = Date.now();
      try {
        const activeFilter = this.showActiveMembers ? true : undefined;
        const backendSortField = this.sortField
          ? this.sortFieldMapping[this.sortField]
          : null;

        const response = await getTargetUsersPaginated(
          activeFilter,
          this.currentPage,
          this.pageSize,
          backendSortField,
          this.sortDirection
        );
        this.members = response.users;
        this.totalItems = response.totalCount;
        this.$emit("update-total", this.totalItems);
      } catch {
        this.showAlert({
          message: "Failed to get target users. Please try again later.",
          type: "dismiss",
        });
      } finally {
        const elapsedTime = Date.now() - startTime;
        const minLoadingTime = 200;
        if (elapsedTime < minLoadingTime) {
          setTimeout(() => {
            this.loading = false;
          }, minLoadingTime - elapsedTime);
        } else {
          this.loading = false;
        }
      }
    },
    async removeUser() {
      try {
        await removeMember(this.memberToDelete.email);
        await this.getMembers();
        this.showAlert({
          message: "User has been removed from the space.",
          type: "success",
        });
        this.closeDeleteModal();
        this.$emit("member-deleted");
      } catch {
        this.closeDeleteModal();
        this.showAlert({
          message: "Failed to remove user from the space.",
          type: "dismiss",
        });
      }
    },
    refreshMembers() {
      this.getMembers();
    },
  },
};
</script>
